import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useLang } from '@/contexts/LangContext';
import { useAuth } from '@/contexts/AuthContext';

export default function CheckEmailPage() {
  const { t } = useLang();
  const { resendVerification } = useAuth();
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const initialEmail = params.get('email') || '';
  const redirectTo = params.get('redirect');

  const [email, setEmail] = useState(initialEmail);
  const [loading, setLoading] = useState(false);

  const handleResend = async () => {
    if (!email) {
      toast.error(t('alerts.required_fields'));
      return;
    }
    setLoading(true);
    try {
      await resendVerification(email);
      toast.success(t('verify.resend_success'));
    } catch (err) {
      toast.error(err.response?.data?.error || t('verify.resend_failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-12">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-lg">
        <Card className="rounded-2xl shadow-sm">
          <CardContent className="p-8 sm:p-10">
            <div className="h-12 w-12 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mb-6">
              <Mail className="h-6 w-6" />
            </div>
            <h1 className="text-2xl font-semibold mb-2">{t('verify.check_title')}</h1>
            <p className="text-sm text-muted-foreground">
              {email ? `${t('verify.check_desc')} ${email}.` : t('verify.check_desc_no_email')}
            </p>

            <div className="mt-6 space-y-3">
              <Input
                type="email"
                className="rounded-lg"
                placeholder={t('auth.email')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <Button className="w-full rounded-xl h-11" onClick={handleResend} disabled={loading || !email}>
                {loading ? t('verify.resending') : t('verify.resend')}
              </Button>
            </div>

            <div className="mt-6 text-center text-sm text-muted-foreground">
              <Link to={redirectTo ? `/login?redirect=${encodeURIComponent(redirectTo)}` : '/login'} className="text-primary font-medium hover:underline">
                {t('verify.back_login')}
              </Link>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
